class CartDrawer {
    constructor() {
        this.drawer = document.getElementById('CartDrawer');
        if (!this.drawer) return;

        this.content = this.drawer.querySelector('.cart-drawer__content');
        this.closeButtons = this.drawer.querySelectorAll('.js-cart-drawer-close');
        this.init();
    }

    init() {
        // Close events
        this.closeButtons.forEach(btn => {
            btn.addEventListener('click', () => this.close());
        });

        // Close on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen()) this.close();
        });

        // Event delegation for actions inside the drawer
        this.drawer.addEventListener('click', (e) => {
            const target = e.target.closest('[data-action]');
            if (!target) return;

            const action = target.dataset.action;
            const key = target.closest('[data-key]')?.dataset.key;
            if (!key) return;

            const input = target.closest('[data-key]').querySelector('.drawer-qty-input');
            let quantity = 0;

            if (action === 'remove') {
                quantity = 0;
            } else if (input) {
                quantity = parseInt(input.value) || 0;
                if (action === 'increase') quantity++;
                else if (action === 'decrease') quantity--;
            }

            if (quantity >= 0) this.updateItem(key, quantity);
        });

        // Manual input change
        this.drawer.addEventListener('change', (e) => {
            if (e.target.classList.contains('drawer-qty-input')) {
                const key = e.target.dataset.key;
                const quantity = parseInt(e.target.value);
                if (quantity >= 0) this.updateItem(key, quantity);
            }
        });
    }

    open() {
        this.drawer.querySelector('.cart-drawer').classList.add('is-open');
        document.body.classList.add('overflow-hidden');
    }

    close() {
        this.drawer.querySelector('.cart-drawer').classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
    }

    isOpen() {
        return this.drawer.querySelector('.cart-drawer').classList.contains('is-open');
    }

    async updateItem(key, quantity) {
        this.drawer.querySelector('.cart-drawer').classList.add('is-loading');

        try {
            const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const response = await fetch(rootUrl + 'cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({ id: key, quantity })
            });

            if (!response.ok) throw new Error('Network response was not ok');

            let cart = await response.json();

            // Check if any free gifts became orphaned
            cart = await this.cleanupOrphanedGifts(cart);

            await this.refresh();

            // Update header bubbles if they exist
            this.updateHeaderBubbles(cart.item_count);

        } catch (error) {
            console.error('Error updating cart:', error);
        } finally {
            this.drawer.querySelector('.cart-drawer').classList.remove('is-loading');
        }
    }

    async cleanupOrphanedGifts(cart) {
        if (!cart || !cart.items || cart.items.length === 0) return cart;

        const gifts = cart.items.filter(item => item.properties && item.properties._gift_with);
        if (gifts.length === 0) return cart;

        const nonGifts = cart.items.filter(item => !item.properties || !item.properties._gift_with);

        // If no regular products remain, all gifts must be removed
        if (nonGifts.length === 0) {
            for (const gift of gifts) {
                await this.removeLineItem(gift.key);
            }
            const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const r = await fetch(rootUrl + 'cart.js');
            return await r.json();
        }

        // For each gift, check if its corresponding parent product is still in the cart
        let giftRemoved = false;
        for (const gift of gifts) {
            const parentType = (gift.properties._gift_with || '').toLowerCase();
            let parentFound = false;

            if (parentType.includes('diffuser-kit') || parentType.includes('kit') || parentType.includes('room')) {
                parentFound = nonGifts.some(item => {
                    const h = (item.handle || '').toLowerCase();
                    const t = (item.title || item.product_title || '').toLowerCase();
                    const v = (item.variant_title || '').toLowerCase();
                    const isKit = h.includes('diffuser') || t.includes('diffuser') || t.includes('starter') || t.includes('kit');
                    const is2PlusRooms = v.includes('2') || v.includes('3') || v.includes('4') || v.includes('duo') || v.includes('three') || v.includes('four') || (item.properties && item.properties._tier && !item.properties._tier.toLowerCase().includes('starter') && !item.properties._tier.toLowerCase().includes('1 room'));
                    return isKit && is2PlusRooms;
                });
            } else if (parentType.includes('bundle')) {
                parentFound = nonGifts.some(item => {
                    const h = (item.handle || '').toLowerCase();
                    const t = (item.title || item.product_title || '').toLowerCase();
                    const v = (item.variant_title || '').toLowerCase();
                    const isBundle = h.includes('bundle') || t.includes('bundle');
                    const is2Rooms = v.includes('2') || (item.properties && item.properties._tier && item.properties._tier.toLowerCase().includes('2'));
                    return isBundle && is2Rooms;
                });
            } else if (parentType.includes('refill')) {
                parentFound = nonGifts.some(item => {
                    const h = (item.handle || '').toLowerCase();
                    const t = (item.title || item.product_title || '').toLowerCase();
                    const v = (item.variant_title || '').toLowerCase();
                    const isRefill = h.includes('refill') || t.includes('refill');
                    const is4Or6Or8 = v.includes('4') || v.includes('6') || v.includes('8') || (item.properties && item.properties._pack && (item.properties._pack.includes('4') || item.properties._pack.includes('6') || item.properties._pack.includes('8')));
                    return isRefill && is4Or6Or8;
                });
            } else {
                parentFound = nonGifts.length > 0;
            }

            if (!parentFound) {
                await this.removeLineItem(gift.key);
                giftRemoved = true;
            }
        }

        if (giftRemoved) {
            const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const r = await fetch(rootUrl + 'cart.js');
            return await r.json();
        }

        return cart;
    }

    async removeLineItem(key) {
        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        await fetch(rootUrl + 'cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ id: key, quantity: 0 })
        });
    }

    async refresh() {
        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        // Fetch the cart drawer section specifically
        const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        const htmlText = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        const newDrawer = doc.querySelector('.cart-drawer');

        if (newDrawer) {
            this.drawer.querySelector('.cart-drawer').innerHTML = newDrawer.innerHTML;
            this.initCloseButtons();
        }

        // Also update cart savings and bubbles
        try {
            const cartRes = await fetch(rootUrl + 'cart.js');
            const cart = await cartRes.json();
            this.updateSavingsUI(cart);
            this.updateHeaderBubbles(cart.item_count);
        } catch(e) {}
    }

    updateSavingsUI(cart) {
        if (!cart) return;
        let totalSavings = 0;
        let hasExplicitSavings = false;
        let displayTotal = 0;

        (cart.items || []).forEach(item => {
            const isGift = item.properties && item.properties._gift_with;
            if (!isGift) {
                displayTotal += (item.final_line_price || item.line_price || 0);
            }
            if (item.properties && item.properties._savings) {
                const s = parseFloat(item.properties._savings);
                if (!isNaN(s)) {
                    totalSavings += s * (item.quantity || 1);
                    hasExplicitSavings = true;
                }
            } else if (isGift) {
                totalSavings += ((item.original_price || item.price) / 100) * (item.quantity || 1);
            } else if (item.original_price && item.final_price && item.original_price > item.final_price) {
                totalSavings += ((item.original_price - item.final_price) / 100) * (item.quantity || 1);
            }
        });

        if (!hasExplicitSavings && displayTotal >= 2999 && totalSavings > 0) {
            totalSavings += 7.99;
        }

        const savingsRow = this.drawer.querySelector('#CartDrawerSavings');
        const savingsEl = this.drawer.querySelector('.cart-drawer__savings-amount');
        if (savingsRow && savingsEl) {
            if (totalSavings > 0) {
                savingsEl.textContent = '$' + totalSavings.toFixed(2);
                savingsRow.style.display = 'flex';
            } else {
                savingsRow.style.display = 'none';
            }
        }
    }

    initCloseButtons() {
        this.drawer.querySelectorAll('.js-cart-drawer-close').forEach(btn => {
            btn.onclick = () => this.close();
        });
    }

    updateHeaderBubbles(count) {
        const bubbles = document.querySelectorAll('.cart-count-bubble');
        bubbles.forEach(bubble => {
            bubble.textContent = count;
            if (count > 0) bubble.classList.remove('hidden');
            else bubble.classList.add('hidden');
        });
    }
}

// Initialize and expose globally for other scripts to use
window.cartDrawer = new CartDrawer();

// Event listener for custom cart:open event if needed
document.addEventListener('cart:open', () => {
    if (window.cartDrawer) window.cartDrawer.open();
});

// Event listener for cart updates from other scripts
document.addEventListener('cart:updated', async (event) => {
    if (window.cartDrawer) {
        await window.cartDrawer.refresh();
        if (event.detail?.open) window.cartDrawer.open();
    }
});
